import { SportType } from '../../models/court/dto/court.dto';

/**
 * The "pricing grid" model — the experimental alternative to the
 * availability-block + slider form (`AddAvailabilitiesForm`).
 *
 * The operator cuts the day into independent **blocks**, each with its own
 * slot duration, display interval and optional flat price. The blocks are
 * flattened into an ordered list of **slots** (the grid columns); the grid's
 * rows are the seven weekdays. Every cell can override the block's flat
 * price, or be cleared so that slot is simply not bookable that day.
 *
 * Nothing here talks to the API. `build-grid-compact-blocks.ts` turns a grid
 * into the `CompactAvailabilityBlockDto[]` the existing compact endpoints
 * already accept, so this feature needs no server change.
 */

export type PricingBlock = {
    id: string;
    /** `HH:MM`, wall clock. */
    start: string;
    /** `HH:MM`, wall clock. `24:00` is allowed and means midnight. */
    end: string;
    /** Length of one bookable slot, in minutes. */
    durationMinutes: number;
    /** Distance between two consecutive slot starts, in minutes. */
    intervalMinutes: number;
    /**
     * Flat price applied to every cell of the block that has no override.
     * Kept as the raw text of the input; `''` means "no flat price" and such
     * cells render as unpriced until the operator fills them.
     */
    priceRaw: string;
};

export type GridSlot = {
    blockId: string;
    /** Minutes from local midnight. */
    startMinute: number;
    endMinute: number;
};

/** Sentinel stored in an override to say "this cell is not bookable". */
export const CLEARED_CELL = 'X';
export type PriceOverride = number | typeof CLEARED_CELL;

/**
 * Per-cell overrides keyed by `priceOverrideKey`. Keyed by sport + day +
 * slot *times* rather than block id, so editing a block's hours keeps every
 * override whose slot still exists, and removing + re-adding a block does
 * not lose them either.
 */
export type PriceOverrides = Record<string, PriceOverride>;

export type PricingGridState = {
    blocks: PricingBlock[];
    overrides: PriceOverrides;
};

/** Grid rows, top to bottom. JS `Date.getDay()` numbers, Monday first. */
export const GRID_DAY_ORDER: readonly number[] = [1, 2, 3, 4, 5, 6, 0];

export const SLOT_DURATION_OPTIONS_MINUTES: readonly number[] = [15, 30, 45, 60, 90, 120];
export const SLOT_INTERVAL_OPTIONS_MINUTES: readonly number[] = [15, 30, 45, 60, 90, 120];

export const MINUTES_PER_DAY = 24 * 60;
const MIN_STEP_MINUTES = 5;

/** `HH:MM` -> minutes from midnight; `NaN` when the text is not a time. */
export function parseTimeToMinutes(hhmm: string): number {
    const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? '');
    if (!match) {
        return NaN;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (minutes > 59 || hours > 24 || (hours === 24 && minutes > 0)) {
        return NaN;
    }
    return hours * 60 + minutes;
}

/** Minutes from midnight -> `HH:MM`. 1440 renders as `24:00`, not `00:00`. */
export function formatMinutes(totalMinutes: number): string {
    const clamped = Math.max(0, Math.min(MINUTES_PER_DAY, Math.round(totalMinutes)));
    const hours = Math.floor(clamped / 60);
    const minutes = clamped % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** The block's `[start, end)` in minutes, or `null` when either time is invalid. */
export function blockRangeMinutes(block: Pick<PricingBlock, 'start' | 'end'>): [number, number] | null {
    const start = parseTimeToMinutes(block.start);
    const end = parseTimeToMinutes(block.end);
    if (Number.isNaN(start) || Number.isNaN(end)) {
        return null;
    }
    return [start, end];
}

/**
 * Ids of every block whose hours overlap another block's OF THE SAME SLOT
 * DURATION. Two blocks on the same hours with different durations are not a
 * conflict: that is how a court offers 60-minute AND 90-minute bookings on
 * the same hours at different prices (one block per duration, the API
 * prices each duration from its own rows).
 */
export function findOverlappingBlockIds(blocks: PricingBlock[]): Set<string> {
    const overlapping = new Set<string>();
    for (let i = 0; i < blocks.length; i++) {
        const a = blockRangeMinutes(blocks[i]);
        if (!a) continue;
        for (let j = i + 1; j < blocks.length; j++) {
            const b = blockRangeMinutes(blocks[j]);
            if (!b) continue;
            if (blocks[i].durationMinutes !== blocks[j].durationMinutes) continue;
            if (a[0] < b[1] && b[0] < a[1]) {
                overlapping.add(blocks[i].id);
                overlapping.add(blocks[j].id);
            }
        }
    }
    return overlapping;
}

/**
 * The grid's columns: every block's slots, in start-time order, with a later
 * block clipped so it begins after the previous block OF THE SAME DURATION
 * ends (the part of an overlapping block that collides is dropped — the
 * editor warns about it). Blocks of different durations never clip each
 * other; the grid shows one duration at a time.
 *
 * A slot is emitted only when it fits entirely inside the block, so a
 * 08:00–10:30 block with 60-minute slots yields 08:00 and 09:00, never a
 * truncated 10:00–10:30.
 */
export function flattenBlocksToSlots(blocks: PricingBlock[]): GridSlot[] {
    const ordered = blocks
        .map((block) => ({ block, range: blockRangeMinutes(block) }))
        .filter((entry): entry is { block: PricingBlock; range: [number, number] } => entry.range !== null)
        .sort((a, b) => a.range[0] - b.range[0]);

    const slots: GridSlot[] = [];
    const cursorByDuration = new Map<number, number>();
    for (const { block, range } of ordered) {
        const duration = Math.max(MIN_STEP_MINUTES, Number(block.durationMinutes) || 60);
        const step = Math.max(MIN_STEP_MINUTES, Number(block.intervalMinutes) || duration);
        const cursor = cursorByDuration.get(duration) ?? 0;
        const start = Math.max(range[0], cursor);
        const end = Math.min(range[1], MINUTES_PER_DAY);
        for (let minute = start; minute + duration <= end; minute += step) {
            slots.push({ blockId: block.id, startMinute: minute, endMinute: minute + duration });
        }
        cursorByDuration.set(duration, Math.max(cursor, end));
    }
    return slots;
}

export function priceOverrideKey(sport: SportType, day: number, slot: Pick<GridSlot, 'startMinute' | 'endMinute'>): string {
    return `${sport}|${day}|${slot.startMinute}|${slot.endMinute}`;
}

/** The block's flat price as a number, or `null` when empty / not a number. */
export function parseBlockPrice(priceRaw: string): number | null {
    if (priceRaw === undefined || priceRaw === null || String(priceRaw).trim() === '') {
        return null;
    }
    const parsed = Number(String(priceRaw).replace(',', '.'));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/**
 * What a cell is worth: its override, else its block's flat price, else
 * `null` (unpriced — the slot will not be bookable that day for that sport).
 */
export function resolveCellPrice(
    state: PricingGridState,
    sport: SportType,
    day: number,
    slot: GridSlot,
): number | null {
    const override = state.overrides[priceOverrideKey(sport, day, slot)];
    if (override === CLEARED_CELL) {
        return null;
    }
    if (typeof override === 'number') {
        return override;
    }
    const block = state.blocks.find((b) => b.id === slot.blockId);
    return block ? parseBlockPrice(block.priceRaw) : null;
}

/** Distinct slot durations across the blocks, ascending. */
export function blockDurations(blocks: PricingBlock[]): number[] {
    return Array.from(new Set(blocks.map((b) => Number(b.durationMinutes)).filter((d) => d > 0))).sort((a, b) => a - b);
}

/** A slot's label, e.g. `08:00–09:00`. */
export function formatSlotLabel(slot: Pick<GridSlot, 'startMinute' | 'endMinute'>): string {
    return `${formatMinutes(slot.startMinute)}–${formatMinutes(slot.endMinute)}`;
}

/**
 * The next block to append: the SAME hours as the previous block, with the
 * next slot duration not yet offered on those hours. Adding a block is most
 * often "also offer 90-minute bookings on these hours", so that is the
 * default; starting the block where the last one ended (10 PM to midnight)
 * was what operators had to undo every time.
 */
export function makeNextBlock(blocks: PricingBlock[], id: string): PricingBlock {
    const last = blocks[blocks.length - 1];
    if (!last) {
        return makeDefaultBlock(id);
    }
    const sameHours = blocks.filter((b) => b.start === last.start && b.end === last.end);
    const usedDurations = new Set(sameHours.map((b) => Number(b.durationMinutes)));
    const after = SLOT_DURATION_OPTIONS_MINUTES.filter((d) => d > Number(last.durationMinutes));
    const before = SLOT_DURATION_OPTIONS_MINUTES.filter((d) => d <= Number(last.durationMinutes));
    const duration =
        [...after, ...before].find((d) => !usedDurations.has(d)) ?? Number(last.durationMinutes);
    return {
        id,
        start: last.start,
        end: last.end,
        durationMinutes: duration,
        intervalMinutes: duration,
        priceRaw: '',
    };
}

export function makeDefaultBlock(id: string): PricingBlock {
    return { id, start: '08:00', end: '22:00', durationMinutes: 60, intervalMinutes: 60, priceRaw: '' };
}
